import moment from 'moment'
import { Calendar } from 'react-native-calendars'

import { Icon } from '../icons'
import { Stack, XStack } from '../layout'
import Pressable from '../pressable'
import { colors } from '../tailwind'
import { B2 } from '../typography'

interface ISelectedDate {
  year: number
  month: number
  day: number
  timestamp: number
  dateString: string
}

interface IMarkedDates {
  [key: string]: object
}

interface IDatePickerProps {
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>
  setStartDate: React.Dispatch<React.SetStateAction<string>>
  setEndDate: React.Dispatch<React.SetStateAction<string>>
  startDate: string
  endDate: string
  isModalVisible: boolean
}

const DatePicker = ({
  startDate,
  endDate,
  setEndDate,
  setStartDate,
  setIsVisible,
  ...porps
}: IDatePickerProps) => {
  const handleDayPress = (day: ISelectedDate) => {
    if (!startDate || (startDate && endDate)) {
      setStartDate(day.dateString)
      setEndDate('')
    } else if (startDate && !endDate) {
      const isEndDateAfterStartDate =
        new Date(day.dateString) > new Date(startDate)
      if (isEndDateAfterStartDate) {
        setEndDate(day.dateString)
      } else {
        setEndDate(startDate)
        setStartDate(day.dateString)
      }
    }
  }

  const markedDates: IMarkedDates = {}
  if (startDate) {
    markedDates[startDate] = {
      customStyles: {
        container: styles.startDateStyle,
        text: styles.textStartStyle,
      },
    }
  }
  if (endDate) {
    markedDates[endDate] = { selected: true, marked: true }
    const range = { start: startDate, end: endDate }
    const daysInRange = enumerateDaysBetweenDates(range.start, range.end)
    daysInRange.forEach((date, index) => {
      if (index === 0) {
        return true
      } else if (index === daysInRange.length - 1) {
        markedDates[date] = {
          customStyles: {
            container: styles.endDateStyle,
            text: styles.textEndStyle,
          },
        }
        return true
      }
      markedDates[date] = {
        customStyles: {
          container: styles.middleDateStyle,
          text: styles.textMiddleStyle,
        },
      }
    })
  }

  function enumerateDaysBetweenDates(startDate: string, endDate: string) {
    const date = []
    while (moment(startDate) <= moment(endDate)) {
      date.push(startDate)
      startDate = moment(startDate).add(1, 'days').format('YYYY-MM-DD')
    }
    return date
  }

  const handleClearPress = () => {
    setStartDate('')
    setEndDate('')
  }

  return (
    <Stack
      className="justify-center rounded-lg bg-white p-2"
      style={{ width: 300 }}
    >
      <Calendar
        onDayPress={handleDayPress}
        markedDates={markedDates}
        markingType={'custom'}
        theme={{
          todayTextColor: '#2075F0',
          selectedDayTextColor: '##fff',
          selectedDayBackgroundColor: '#2075F0',
          calendarBackground: '#fff',
          arrowColor: '#f00',
          monthTextColor: '#000',
          textDayFontWeight: '500',
          textMonthFontWeight: 'bold',
          textDayHeaderFontWeight: '400',
          textDayFontSize: 16,
          textMonthFontSize: 16,
          textDayHeaderFontSize: 12,
        }}
        renderArrow={(direction) => {
          if (direction === 'left') {
            return <Icon type={'ArrowLeft'} />
          } else {
            return <Icon type={'ArrowRight'} />
          }
        }}
        horizontal={false}
        firstDay={1}
        {...porps}
      />
      <Stack className="flex-1 p-2">
        <XStack className="items-center justify-between">
          <Pressable onPress={handleClearPress}>
            <B2 className="text-grays-80">Clear All</B2>
          </Pressable>
          <Pressable
            className="bg-primary-100 rounded px-3 py-1"
            onPress={() => setIsVisible(false)}
          >
            <B2 className="text-white">Apply</B2>
          </Pressable>
        </XStack>
      </Stack>
    </Stack>
  )
}

export default DatePicker

const styles = {
  startDateStyle: {
    backgroundColor: colors.primary[100],
    elevation: 2,
    width: '100%',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 0,
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 0,
  },
  middleDateStyle: {
    backgroundColor: colors.primary[30],
    elevation: 2,
    width: '100%',
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  textMiddleStyle: {
    fontSize: 15,
    color: colors.grays[100],
  },
  textStartStyle: {
    fontSize: 15,
    color: colors.neutral.white100,
  },
  textEndStyle: {
    fontSize: 15,
    color: colors.neutral.white100,
    borderBottomWidth: 1,
    borderBottomColor: colors.neutral.white100,
  },
  disabledDateStyle: {
    backgroundColor: 'transparent',
  },
  endDateStyle: {
    backgroundColor: colors.primary[100],
    elevation: 2,
    width: '100%',
    borderTopLeftRadius: 0,
    borderTopRightRadius: 20,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 20,
  },
}
